import { RngStreams } from '../../core/rng/RngStreams';
import { StatSheet, type StatModifier } from '../stats/StatSheet';
import { makeAbility, type AbilityId, type AbilityInstance } from '../abilities/AbilityDefs';
import { TITHE_CAP, TITHE_RATE } from '../abilities/skillHooks';
import { BANISHES_PER_RUN } from './skillOffers';
import type { ArtifactProc } from '../artifacts/ArtifactDefs';
import { CHARACTERS, DEFAULT_CHARACTER, type CharacterId } from '../characters/CharacterDefs';

const WORDS_A = ['ash', 'grim', 'pale', 'dusk', 'hollow', 'iron', 'vile', 'dread', 'mourn', 'rift'];
const WORDS_B = ['spire', 'crypt', 'maw', 'stair', 'depth', 'gate', 'veil', 'fane', 'pit', 'throne'];

export function randomSeed(): string {
  const a = WORDS_A[Math.floor(Math.random() * WORDS_A.length)]!;
  const b = WORDS_B[Math.floor(Math.random() * WORDS_B.length)]!;
  return `${a}-${b}-${Math.floor(Math.random() * 100)}`;
}

export interface EquippedArtifact {
  artifactId: string;
  quality: number;
  /** Drop proc — run-only, never banked. Brought artifacts are 'none'. */
  proc: ArtifactProc;
  curseId: string | null;
}

/** Base-form artifact from the collection (procs never persist). */
export interface BroughtArtifact {
  artifactId: string;
  quality: number;
}

/** Meta progression carried into a run from the profile. Defaults = fresh account. */
export interface RunMetaOptions {
  /** ≤2 from the collection, at banked quality. SAFE — never lost. */
  broughtArtifacts?: BroughtArtifact[];
  artifactCapacity?: number;
  /** Resolved meta upgrade stat mods (metaStatModifiers) — 'meta' source, run-permanent. */
  metaStatMods?: StatModifier[];
  pocket?: 'mend' | null;
  /** Starting character (signature ability + stat variance). Default: arbalist. */
  character?: CharacterId;
  /** Difficulty ladder tier (tiers.ts). 0 = base game, identity scaling. */
  tier?: number;
}

/**
 * Cross-floor run state. The World (entities, pools, map) is rebuilt per
 * floor; this object persists: seed streams, floor number, gems, abilities,
 * artifacts, summon bar, and the run's meta smoothing.
 */
export class RunState {
  floor = 1;
  /**
   * Authoritative whole-run sim clock (seconds). Accumulated by World.update
   * — persists across floors and the vault since the World is rebuilt per
   * floor. Drives the enrage timer, the HUD countdown, and run-end records.
   */
  timeSeconds = 0;
  /**
   * Kill-fed bar (monotonic fill points): drives the 5 pick thresholds and
   * the Summon Bar. Only ever grows via addBarFill.
   */
  barFill = 0;
  /** Total kills this run (all types) — feeds the lifetime unlock counters. */
  kills = 0;
  /** Monotonic gem total: drives pickup stat bumps and the run-end tithe. */
  gemsEarned = 0;
  /** Pickup-track bumps already granted (World.tickPickupBumps). */
  pickupBumpsGranted = 0;
  /**
   * Gems collected since the last descent — the doc's "unbanked gold". The
   * stake that Tithe scales off and that Toll/Shared Fate can forfeit.
   * Deductions hit THIS and gemsHeld, NEVER gemsEarned (monotonic).
   */
  unbankedGems = 0;
  /**
   * Settlement (Tithe upgrade): the damage bonus held at the moment of
   * descent, waiting to land as the next floor's arrival shock. Consumed by
   * skillOnFloorEnter.
   */
  titheSettlement = 0;

  // ---- Run-persistent aspect scalars (per-floor entities live on World) ----
  /** Wick's Guttering candle: 1 full → 0 guttered; refilled by descent,
   *  topped up by content interactions (relightCandle). */
  candle = 1;
  /** Wick's Flare (MA rework): seconds of spent-candle fury left. Lives here
   *  (not World) because aspectDamageMult folds from RunState. */
  flareLeft = 0;
  /** Vane's Ninefold: one refused death per floor, refreshed by descending. */
  reviveReady = true;
  /** Mourne's Ossuary: banked corpses, persists through descent (his edge). */
  corpseReserve = 0;
  /** Cask's powder burns (M0): scorched max HP — folded into the derived
   *  maxHp at World.syncPlayerStats, never lethal. Descent restores the
   *  CEILING only (round 5): burnCreditHold suppresses the raise-heals-delta
   *  rule for exactly one syncPlayerStats. */
  burnedHp = 0;
  burnCreditHold = false;
  /** Wardstone charges (shop, 2026-07-27): each absorbs one landing hit.
   *  Floor-scoped — onDescend clears. */
  wardCharges = 0;
  /** Cartographer's Sketch (shop): the minimap reveals this floor. */
  floorIntel = false;
  /** Spendable wallet (shops). */
  gemsHeld = 0;
  god = false;
  /** Slot 0 is the character's locked signature ability (set in ctor). */
  abilities: AbilityInstance[];
  readonly character: CharacterId;
  readonly stats = new StatSheet();
  picksConsumed = 0;
  /** Escalating-cost reroll counter (skillOffers.rerollCost), run-wide. */
  rerollsUsed = 0;
  /** Free banishes remaining (BANISHES_PER_RUN at start). */
  banishesLeft = BANISHES_PER_RUN;
  /** Skills struck from all future offers this run. */
  readonly banished = new Set<AbilityId>();
  /** Equipped artifacts with their quality rolls. */
  artifacts: EquippedArtifact[] = [];
  /** Ids of brought (SAFE) artifacts — locked against swapping/loss. */
  readonly broughtIds: Set<string>;
  /**
   * artifactId → quality already settled into the profile at the last
   * checkpoint (run start for brought artifacts, then each descent).
   * Copies at or below this quality are SAFE — not at risk, not re-bankable.
   */
  readonly bankedQuality: Map<string, number>;
  /** Max equipped artifacts (5 + meta slot unlocks). */
  readonly artifactCapacity: number;
  /** Difficulty ladder tier, fixed for the whole run. */
  readonly tier: number;
  pocket: 'mend' | null;
  /** Without-replacement drop pool, across the whole run. */
  readonly droppedArtifacts = new Set<string>();
  /** Stat-modifier sources that expire when leaving the floor. */
  readonly floorBuffs = new Set<string>();
  /**
   * Shrine curses taken this run (permanent; never in floorBuffs). Each
   * entry's `source` is individually removable — the vault cleansing altar
   * cleanses one instance.
   */
  readonly shrineCurses: { curseId: string; source: string }[] = [];

  /** Called on descent: floor-scoped buffs end. */
  clearFloorBuffs(): void {
    for (const source of this.floorBuffs) this.stats.removeSource(source);
    this.floorBuffs.clear();
  }
  readonly streams: RngStreams;

  constructor(readonly seed: string, meta: RunMetaOptions = {}) {
    this.streams = new RngStreams(seed);
    this.broughtIds = new Set((meta.broughtArtifacts ?? []).map((a) => a.artifactId));
    this.bankedQuality = new Map((meta.broughtArtifacts ?? []).map((a) => [a.artifactId, a.quality]));
    this.artifactCapacity = meta.artifactCapacity ?? 5;
    this.tier = Math.max(0, Math.floor(meta.tier ?? 0));
    this.pocket = meta.pocket ?? null;
    this.character = meta.character ?? DEFAULT_CHARACTER;
    const charDef = CHARACTERS[this.character];
    this.abilities = [makeAbility(charDef.signatureAbility)];
    if (charDef.statMods.length > 0) this.stats.addModifiers('character', charDef.statMods);
    // Meta upgrade tracks: run-permanent source, never in floorBuffs.
    if (meta.metaStatMods && meta.metaStatMods.length > 0) {
      this.stats.addModifiers('meta', meta.metaStatMods);
    }
  }

  /**
   * Artifacts at risk / bankable at the next checkpoint: everything equipped
   * whose quality exceeds what's already been settled (unowned counts as
   * exceeded). Banked-at-current-quality copies are SAFE.
   */
  get foundArtifacts(): EquippedArtifact[] {
    return this.artifacts.filter((a) => {
      const bankedQ = this.bankedQuality.get(a.artifactId);
      return bankedQ === undefined || a.quality > bankedQ;
    });
  }

  /**
   * Checkpoint passed: these copies settled (banked or converted) — record
   * them so they leave the at-risk set. Equal/worse copies that merely
   * converted to shards MUST be marked too, or they'd re-convert every floor.
   */
  markBanked(settled: readonly { artifactId: string; quality: number }[]): void {
    for (const s of settled) this.bankedQuality.set(s.artifactId, s.quality);
  }

  equippedQuality(artifactId: string): number | undefined {
    return this.artifacts.find((a) => a.artifactId === artifactId)?.quality;
  }

  /**
   * The single kill-bar funnel — every fill source (kills, Knell) routes
   * through here so the killFillMult stat applies uniformly.
   */
  addBarFill(points: number): void {
    this.barFill += points * this.stats.resolve('killFillMult');
  }

  collectGems(value: number): void {
    this.gemsEarned += value;
    this.gemsHeld += value;
    this.unbankedGems += value;
  }

  /**
   * S5 descent hook: the departing floor's stakes settle and per-floor
   * character resources refresh. Called ONLY on a real descent (RunScene) —
   * never on vault enter/exit. Aspect refreshes (candle, revive, fuse,
   * hound) attach here in M9.
   */
  onDescend(): void {
    const tithe = this.abilities.find((a) => a.defId === 'tithe');
    this.titheSettlement = tithe?.upgraded
      ? Math.min(TITHE_CAP, this.unbankedGems * TITHE_RATE)
      : 0;
    this.unbankedGems = 0;
    // Aspect refreshes: descent IS the reset button (Wick's refill, Vane's
    // heal; the hound revives via the per-floor World rebuild). Cask's
    // burns: the scorched CEILING knits shut but the flesh does NOT — the
    // hold flag suppresses syncPlayerStats' raise-heals-delta credit once
    // (round 5, Sarah: "heal back to full every floor doesn't seem right").
    this.candle = 1;
    this.flareLeft = 0;
    this.reviveReady = true;
    if (this.character === 'cask' && this.burnedHp > 0) this.burnCreditHold = true;
    this.burnedHp = 0;
    // Floor-scoped shop purchases die with the floor they were bought for.
    this.wardCharges = 0;
    this.floorIntel = false;
  }

  /**
   * Wallet-only credit (payouts, refunds). NEVER route through collectGems —
   * gemsEarned drives pickup stat bumps and the run-end shard tithe; paying
   * a payout there makes it self-feeding.
   */
  addHeldGems(value: number): void {
    this.gemsHeld += value;
  }
}
